import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    FileText,
    Image as ImageIcon,
    Mountain,
    MapPin,
    RefreshCw,
    AlertCircle,
    Target,
    Plane,
    Truck,
    History,
    Shield,
    Cpu,
    Award,
    Heart,
    Handshake,
    Eye,
    Settings,
    Grid,
    Info,
    Activity,
    Compass,
    Mic2,
    Trophy,
    Users,
    Calendar,
    Gauge,
    Package,
    ChevronDown,
    ChevronUp,
    Briefcase,
    Wrench,
    Map,
    Trash2,
    Plus
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminTransport: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
    const [activeSection, setActiveSection] = useState<'banner' | 'about' | 'services' | 'qhse' | 'branches' | 'values' | 'code' | 'approach' | 'tech' | 'awards' | 'clients' | 'contact' | 'gallery'>('banner');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({ Transport: {} });
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | '' }>({ text: '', type: '' });
    const [activeTransportBranches, setActiveTransportBranches] = useState<number[]>([]);
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            if (!data.Transport) data.Transport = {};
            
            // Set default content for Innovation Tech (Customer Advantages) if blank
            if (!data.Transport.tech_bn_1_title) {
                data.Transport.tech_bn_1_title = "Delivery Accuracy";
                data.Transport.tech_bn_1_desc = "Improved delivery accuracy through real-time fleet visibility.";
                data.Transport.tech_bn_2_title = "Client Response";
                data.Transport.tech_bn_2_desc = "Faster response and communication between operations and clients.";
                data.Transport.tech_bn_3_title = "Safety Insights";
                data.Transport.tech_bn_3_desc = "Data-driven insights enhance safety, sustainability, and reliability.";
                data.Transport.tech_benefit_title = "Customer Advantages";
                data.Transport.tech_strategic_label = "Strategic Value";
            }
            
            setContent(data);
            if (data.Transport) {
                const ids = [];
                for (let i = 1; i <= 40; i++) {
                    if (data.Transport[`br_${i}_name`]) {
                        ids.push(i);
                    }
                }
                setActiveTransportBranches(ids);
            }
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const updateField = (key: string, value: any) => {
        setContent((prev: any) => ({
            ...prev,
            Transport: {
                ...prev.Transport,
                [key]: value
            }
        }));
    };

    const saveSectionText = async (sectionId: string) => {
        setSaving(sectionId);
        try {
            const transportData = content.Transport || {};
            const fieldsToSave: { [key: string]: string } = {};

            if (sectionId === 'banner') {
                fieldsToSave['banner_title'] = transportData.banner_title ?? '';
                fieldsToSave['banner_subtitle'] = transportData.banner_subtitle ?? '';
            } else if (sectionId === 'hero') {
                fieldsToSave['banner_title'] = transportData.banner_title ?? '';
                fieldsToSave['banner_subtitle'] = transportData.banner_subtitle ?? '';
            } else if (sectionId === 'about') {
                fieldsToSave['about_title'] = transportData.about_title ?? '';
                fieldsToSave['about_subtitle'] = transportData.about_subtitle ?? '';
                fieldsToSave['about_desc'] = transportData.about_desc ?? '';
            } else if (sectionId === 'services') {
                fieldsToSave['services_title'] = transportData.services_title ?? '';
                [1, 2, 3].forEach(id => {
                    fieldsToSave[`svc_${id}_title`] = transportData[`svc_${id}_title`] ?? '';
                    fieldsToSave[`svc_${id}_desc`] = transportData[`svc_${id}_desc`] ?? '';
                });
            } else if (sectionId === 'values') {
                fieldsToSave['values_title'] = transportData.values_title ?? '';
                fieldsToSave['values_desc'] = transportData.values_desc ?? '';
                [1, 2, 3, 4].forEach(id => {
                    fieldsToSave[`val_${id}_title`] = transportData[`val_${id}_title`] ?? '';
                    fieldsToSave[`val_${id}_desc`] = transportData[`val_${id}_desc`] ?? '';
                    fieldsToSave[`val_${id}_icon`] = transportData[`val_${id}_icon`] ?? 'Users';
                });
            } else if (sectionId === 'code') {
                fieldsToSave['code_title'] = transportData.code_title ?? '';
                fieldsToSave['code_desc'] = transportData.code_desc ?? '';
                fieldsToSave['code_footer'] = transportData.code_footer ?? '';
                [1, 2, 3, 4].forEach(id => {
                    fieldsToSave[`code_${id}_title`] = transportData[`code_${id}_title`] ?? '';
                    fieldsToSave[`code_${id}_desc`] = transportData[`code_${id}_desc`] ?? '';
                    fieldsToSave[`code_${id}_icon`] = transportData[`code_${id}_icon`] ?? (id === 1 ? 'Trophy' : id === 2 ? 'Handshake' : id === 3 ? 'Heart' : 'Eye');
                });
            } else if (sectionId === 'approach') {
                fieldsToSave['approach_title'] = transportData.approach_title ?? '';
                fieldsToSave['approach_intro'] = transportData.approach_intro ?? '';
                fieldsToSave['approach_footer'] = transportData.approach_footer ?? '';
                fieldsToSave['approach_tagline'] = transportData.approach_tagline ?? '';
                [1, 2, 3, 4, 5].forEach(id => {
                    fieldsToSave[`app_${id}_title`] = transportData[`app_${id}_title`] ?? '';
                    fieldsToSave[`app_${id}_desc`] = transportData[`app_${id}_desc`] ?? '';
                    fieldsToSave[`app_${id}_icon`] = transportData[`app_${id}_icon`] ?? 'Target';
                    fieldsToSave[`app_${id}_color`] = transportData[`app_${id}_color`] ?? 'blue';
                });
            } else if (sectionId === 'qhse') {
                fieldsToSave['qhse_title'] = transportData.qhse_title ?? "Health, Safety & Environment (QHSE)";
                fieldsToSave['qhse_desc_top'] = transportData.qhse_desc_top ?? "Whilst Quality, Health, Safety and Environmental considerations (QHSE) form part of our everyday lives, HTD strongly focusses on this also being part of our working lives. Staff welfare and a positive safety culture are very close to our hearts, this enforced from the Senior Management Team down and embraced by all.";
                fieldsToSave['qhse_desc_mid'] = transportData.qhse_desc_mid ?? "How do we ensure our “One Team, One Goal” moto is effectively communicated, we adopt a route and branch approach, comprising of:";
                fieldsToSave['qhse_desc_bottom'] = transportData.qhse_desc_bottom ?? "Underpinning and supporting our Culture and methodology are our ISO Standards, HTD have held ISO 9001:2015; ISO14001:2015 and ISO45001:2018 accreditation since 2022, quality doctrine underpins our operational execution.\n\nHowever, we must forget our Staff, a diverse family that we need to consider at all times, operating in challenging conditions. The use of dynamic dashboards accessible via multiple locations with role-based user accounts, training material designed in a suitable language and format, training delivered at the correct levels and at a pace that ensures it's understood, all help HTD to ensure we do not sacrifice safety for speed!";
            } else if (sectionId === 'tech') {
                fieldsToSave['tech_title'] = transportData.tech_title ?? '';
                fieldsToSave['tech_desc'] = transportData.tech_desc ?? '';
                [1, 2, 3, 4].forEach(id => {
                    fieldsToSave[`tech_sys_${id}_title`] = transportData[`tech_sys_${id}_title`] ?? '';
                    fieldsToSave[`tech_sys_${id}_desc`] = transportData[`tech_sys_${id}_desc`] ?? '';
                    fieldsToSave[`tech_sys_${id}_icon`] = transportData[`tech_sys_${id}_icon`] ?? 'Gauge';
                });
                [1, 2, 3].forEach(id => {
                    fieldsToSave[`tech_bn_${id}_title`] = transportData[`tech_bn_${id}_title`] ?? '';
                    fieldsToSave[`tech_bn_${id}_desc`] = transportData[`tech_bn_${id}_desc`] ?? '';
                });
                fieldsToSave['tech_benefit_title'] = transportData.tech_benefit_title ?? 'Customer Advantages';
                fieldsToSave['tech_strategic_label'] = transportData.tech_strategic_label ?? 'Strategic Value';
            } else if (sectionId === 'awards') {
                fieldsToSave['awards_title'] = transportData.awards_title ?? '';
                fieldsToSave['awards_desc'] = transportData.awards_desc ?? '';
                fieldsToSave['award_footer'] = transportData.award_footer ?? '';
                [1, 2, 3, 4, 5, 6].forEach(id => {
                    fieldsToSave[`award_pt_${id}_text`] = transportData[`award_pt_${id}_text`] ?? '';
                    fieldsToSave[`award_pt_${id}_icon`] = transportData[`award_pt_${id}_icon`] ?? 'Award';
                });
            } else if (sectionId === 'clients') {
                fieldsToSave['clients_title'] = transportData.clients_title ?? '';
                fieldsToSave['clients_desc'] = transportData.clients_desc ?? '';
                fieldsToSave['clients_list_text'] = transportData.clients_list_text ?? '';
                fieldsToSave['clients_logo_urls'] = transportData.clients_logo_urls ?? '';
                fieldsToSave['clients_footer'] = transportData.clients_footer ?? '';
                fieldsToSave['clients_footer_desc'] = transportData.clients_footer_desc ?? '';
            } else if (sectionId === 'contact') {
                fieldsToSave['contact_address'] = transportData.contact_address ?? '';
                fieldsToSave['contact_phone'] = transportData.contact_phone ?? '';
                fieldsToSave['contact_fax'] = transportData.contact_fax ?? '';
                fieldsToSave['contact_email'] = transportData.contact_email ?? '';
            } else if (sectionId === 'history') {
                fieldsToSave['history_title'] = transportData.history_title ?? '';
                fieldsToSave['history_subtitle'] = transportData.history_subtitle ?? '';
                [1, 2, 3, 4, 5].forEach(id => {
                    fieldsToSave[`hist_${id}_title`] = transportData[`hist_${id}_title`] ?? '';
                    fieldsToSave[`hist_${id}_desc`] = transportData[`hist_${id}_desc`] ?? '';
                    fieldsToSave[`hist_${id}_icon`] = transportData[`hist_${id}_icon`] ?? 'History';
                });
            } else if (sectionId === 'gallery') {
                fieldsToSave['gallery_title'] = transportData.gallery_title ?? '';
            } else if (sectionId === 'branches') {
                for (let i = 1; i <= 40; i++) {
                    if (transportData[`br_${i}_name`] === undefined) continue;
                    ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => {
                        fieldsToSave[`br_${i}_${f}`] = transportData[`br_${i}_${f}`] ?? '';
                    });
                }
            }

            const promises = Object.entries(fieldsToSave).map(async ([key, value]) => {
                const response = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({ 
                        section: 'Transport', 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: transportData[`${key}_ar`] || null
                    })
                });
                return response;
            });

            await Promise.all(promises);
            setMessage({ text: 'Transport profile updated!', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (err) {
            setMessage({ text: 'Sync failed.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({ ...prev, [key]: { file, preview } }));
    };

    const handleMultipleImageSelect = (prefix: string, files: FileList) => {
        const newPending = { ...pendingImages };
        Array.from(files).forEach((file, index) => {
            const preview = URL.createObjectURL(file);
            newPending[`${prefix}_${index + 1}`] = { file, preview };
        });
        setPendingImages(newPending);
    };

    const saveAllPendingImages = async () => {
        if (Object.keys(pendingImages).length === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Transport');
                formData.append('content_key', key);
                formData.append('image', pending.file);
                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                    body: formData
                });
                return response;
            });

            await Promise.all(promises);
            setMessage({ text: 'Media assets deployed!', type: 'success' });
            setPendingImages({});
            fetchContent();
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (err) {
            setMessage({ text: 'Media upload failed.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const handleDeleteGalleryKey = async (key: string) => {
        if (!window.confirm('Delete this gallery image?')) return;
        try {
            setSaving('gallery');
            const token = localStorage.getItem('adminToken');
            await fetch('/api/homepage/delete-key', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ section: 'Transport', content_key: key })
            });
            setContent((prev: any) => {
                const copy = { ...prev.Transport };
                delete copy[key];
                return { ...prev, Transport: copy };
            });
            setMessage({ text: 'Gallery image deleted.', type: 'success' });
        } catch (err) {
            console.error(err);
            setMessage({ text: 'Failed to delete image.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    const Label = ({ children }: any) => (
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 block">{children}</label>
    );

    const UploadBox = ({ keyName, label, currentImage, onDelete }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;
        return (
            <div className="group bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                    <div className="flex items-center gap-2">
                        {currentImage && !pending && (
                            <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-2 py-1 rounded-lg">
                                <CheckCircle2 className="w-3 h-3" />
                                <span className="text-[8px] font-black uppercase">Live</span>
                            </div>
                        )}
                        {onDelete && (
                            <button
                                onClick={onDelete}
                                className="flex items-center gap-1 px-2 py-1 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-all"
                                title="Delete image"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-2xl overflow-hidden mb-5 border border-slate-100 flex items-center justify-center ring-offset-4 ring-blue-500/0 group-hover:ring-4 transition-all">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Preview" />
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <ImageIcon className="w-8 h-8 text-slate-200" />
                            <span className="text-[10px] text-slate-300 font-bold uppercase">No Image</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <label className="cursor-pointer bg-white text-blue-600 p-4 rounded-2xl font-bold shadow-2xl flex items-center space-x-2 transition-transform hover:scale-105">
                            <RefreshCw className="w-4 h-4" />
                            <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && handleImageSelect(keyName, e.target.files[0])} />
                        </label>
                    </div>
                </div>
                {pending && <p className="text-amber-500 text-[8px] font-black uppercase text-center animate-pulse">Pending Save</p>}
            </div>
        );
    };

    const sections = [
        { id: 'banner', label: 'Hero Banner', icon: Layout },
        { id: 'about', label: 'Enterprise Profile', icon: FileText },
        { id: 'services', label: 'Services', icon: Wrench },
        { id: 'qhse', label: 'Safety (QHSE)', icon: Shield },
        { id: 'branches', label: 'Branch Network', icon: Map },
        { id: 'values', label: 'Core Values', icon: Heart },
        { id: 'code', label: 'Code of Practice', icon: Shield },
        { id: 'approach', label: 'Operational Approach', icon: Compass },
        { id: 'tech', label: 'Innovation Tech', icon: Cpu },
        { id: 'awards', label: 'Awards & Honors', icon: Award },
        { id: 'clients', label: 'Clients', icon: Users },
        { id: 'contact', label: 'Contact Info', icon: Mail },
        { id: 'gallery', label: 'Gallery', icon: Grid },
    ];

    const navItems = [
        { id: 'dashboard', label: 'Homepage Editor', icon: Home, path: '/admin/dashboard' },
        { id: 'header', label: 'Header Editor', icon: Type, path: '/admin/header' },
        { id: 'footer', label: 'Footer Editor', icon: PanelBottom, path: '/admin/footer' },
        { id: 'contact', label: 'Contact Editor', icon: Mail, path: '/admin/contact' },
        { id: 'about', label: 'About Group Editor', icon: Info, path: '/admin/about' },
        { id: 'careers', label: 'Careers Editor', icon: Briefcase, path: '/admin/careers' },
        { id: 'mining', label: 'Mining Editor', icon: Mountain, path: '/admin/mining' },
        { id: 'transport', label: 'Transport Editor', icon: Truck, path: '/admin/transport' },
        { id: 'construction', label: 'Construction Editor', icon: HardHat, path: '/admin/construction' },
        { id: 'exchange', label: 'Exchange Editor', icon: DollarSign, path: '/admin/exchange' },
        { id: 'gabbro', label: 'Gabbro Editor', icon: Zap, path: '/admin/gabbro' },
        { id: 'plaza', label: 'Plaza Hotel Editor', icon: Building2, path: '/admin/plaza' },
        { id: 'realestate', label: 'Real Estate Editor', icon: MapPin, path: '/admin/realestate' },
        { id: 'tyres', label: 'Tyres Editor', icon: Target, path: '/admin/tyres' },
        { id: 'travel', label: 'Travel Editor', icon: Plane, path: '/admin/travel' },
    ];

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Standard Primary Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50 relative">
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Truck className="w-6 h-6 text-blue-600" /> Transport Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('text')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'text' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Text Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        {saving && (
                            <div className="flex items-center space-x-2 text-blue-600 font-bold text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span className="animate-pulse">Syncing...</span>
                            </div>
                        )}
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Global Admin</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center font-bold text-white border border-slate-700 shadow-inner">
                            {username[0]}
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-6xl mx-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-40">
                                <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin mb-6" />
                                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Loading...</p>
                            </div>
                        ) : (
                            <AnimatePresence mode="wait">
                                {activeTab === 'text' ? (
                                    <motion.div key="text" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
                                        
                                        {/* Section Navigation Tabs */}
                                        <div className="flex flex-wrap gap-3 mb-8">
                                            {sections.map(sec => (
                                                <button
                                                    key={sec.id}
                                                    onClick={() => setActiveSection(sec.id as any)}
                                                    className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all border ${activeSection === sec.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                                >
                                                    <sec.icon className="w-4 h-4" />
                                                    <span>{sec.label}</span>
                                                </button>
                                            ))}
                                        </div>

                                        {message.text && (
                                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                                {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                                {message.text}
                                            </motion.div>
                                        )}

                                        <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                            <div className="flex items-center justify-between mb-8">
                                                <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                    <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                    {sections.find(s => s.id === activeSection)?.label} Configuration
                                                </h3>
                                                <button
                                                    onClick={() => saveSectionText(activeSection)}
                                                    disabled={!!saving}
                                                    className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95"
                                                >
                                                    {saving === activeSection ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                    <span>Update {sections.find(s => s.id === activeSection)?.label}</span>
                                                </button>
                                            </div>

                                            {/* === BANNER SECTION === */}
                                            {activeSection === 'banner' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Hero Main Title"
                                                        englishValue={content.Transport?.banner_title}
                                                        arabicValue={content.Transport?.banner_title_ar}
                                                        onChangeEnglish={val => updateField('banner_title', val)}
                                                        onChangeArabic={val => updateField('banner_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Hero Subtitle"
                                                        englishValue={content.Transport?.banner_subtitle}
                                                        arabicValue={content.Transport?.banner_subtitle_ar}
                                                        onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                        onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                        rows={3}
                                                    />
                                                </div>
                                            )}

                                            {/* === ABOUT SECTION === */}
                                            {activeSection === 'about' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Intro Title"
                                                        englishValue={content.Transport?.about_title}
                                                        arabicValue={content.Transport?.about_title_ar}
                                                        onChangeEnglish={val => updateField('about_title', val)}
                                                        onChangeArabic={val => updateField('about_title_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Subtitle"
                                                        englishValue={content.Transport?.about_subtitle}
                                                        arabicValue={content.Transport?.about_subtitle_ar}
                                                        onChangeEnglish={val => updateField('about_subtitle', val)}
                                                        onChangeArabic={val => updateField('about_subtitle_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Full Description"
                                                        englishValue={content.Transport?.about_desc}
                                                        arabicValue={content.Transport?.about_desc_ar}
                                                        onChangeEnglish={val => updateField('about_desc', val)}
                                                        onChangeArabic={val => updateField('about_desc_ar', val)}
                                                        rows={6}
                                                    />
                                                </div>
                                            )}

                                            {/* === SERVICES SECTION === */}
                                            {activeSection === 'services' && (
                                                <ServiceCardManager
                                                    section="Transport"
                                                    data={content.Transport || {}}
                                                    onSaved={fetchContent}
                                                    sectionTitle={content.Transport?.services_title}
                                                    sectionTitleAr={content.Transport?.services_title_ar}
                                                    onChangeSectionTitle={val => updateField('services_title', val)}
                                                    onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                                />
                                            )}

                                            {/* === SAFETY (QHSE) SECTION === */}
                                            {activeSection === 'qhse' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Safety Title"
                                                        englishValue={content.Transport?.qhse_title ?? "Health, Safety & Environment (QHSE)"}
                                                        arabicValue={content.Transport?.qhse_title_ar}
                                                        onChangeEnglish={val => updateField('qhse_title', val)}
                                                        onChangeArabic={val => updateField('qhse_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="First Paragraph (Intro)"
                                                        englishValue={content.Transport?.qhse_desc_top ?? "Whilst Quality, Health, Safety and Environmental considerations (QHSE) form part of our everyday lives, HTD strongly focusses on this also being part of our working lives. Staff welfare and a positive safety culture are very close to our hearts, this enforced from the Senior Management Team down and embraced by all."}
                                                        arabicValue={content.Transport?.qhse_desc_top_ar}
                                                        onChangeEnglish={val => updateField('qhse_desc_top', val)}
                                                        onChangeArabic={val => updateField('qhse_desc_top_ar', val)}
                                                        rows={3}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Middle Paragraph"
                                                        englishValue={content.Transport?.qhse_desc_mid ?? "How do we ensure our “One Team, One Goal” moto is effectively communicated, we adopt a route and branch approach, comprising of:"}
                                                        arabicValue={content.Transport?.qhse_desc_mid_ar}
                                                        onChangeEnglish={val => updateField('qhse_desc_mid', val)}
                                                        onChangeArabic={val => updateField('qhse_desc_mid_ar', val)}
                                                        rows={2}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Second Paragraph (Main Content)"
                                                        englishValue={content.Transport?.qhse_desc_bottom ?? "Underpinning and supporting our Culture and methodology are our ISO Standards, HTD have held ISO 9001:2015; ISO14001:2015 and ISO45001:2018 accreditation since 2022, quality doctrine underpins our operational execution.\n\nHowever, we must forget our Staff, a diverse family that we need to consider at all times, operating in challenging conditions. The use of dynamic dashboards accessible via multiple locations with role-based user accounts, training material designed in a suitable language and format, training delivered at the correct levels and at a pace that ensures it's understood, all help HTD to ensure we do not sacrifice safety for speed!"}
                                                        arabicValue={content.Transport?.qhse_desc_bottom_ar}
                                                        onChangeEnglish={val => updateField('qhse_desc_bottom', val)}
                                                        onChangeArabic={val => updateField('qhse_desc_bottom_ar', val)}
                                                        rows={8}
                                                    />
                                                </div>
                                            )}

                                            {/* === BRANCHES SECTION === */}
                                            {activeSection === 'branches' && (() => {
                                                const T = content.Transport || {};
                                                const getBranches = () => {
                                                    return activeTransportBranches.map(i => ({
                                                        i,
                                                        name: T[`br_${i}_name`] || '',
                                                        addr: T[`br_${i}_addr`] || '',
                                                        phone: T[`br_${i}_phone`] || '',
                                                        fax: T[`br_${i}_fax`] || '',
                                                        coord: T[`br_${i}_coord`] || ''
                                                    }));
                                                };
                                                const branches = getBranches();
                                                const nextI = activeTransportBranches.length > 0 ? Math.max(...activeTransportBranches) + 1 : 1;
                                                const handleAdd = () => {
                                                    updateField(`br_${nextI}_name`, 'New Branch');
                                                    updateField(`br_${nextI}_addr`, '');
                                                    updateField(`br_${nextI}_phone`, '');
                                                    updateField(`br_${nextI}_fax`, '');
                                                    updateField(`br_${nextI}_coord`, '');
                                                    setActiveTransportBranches(prev => [...prev, nextI]);
                                                };
                                                const handleDelete = (idx: number) => {
                                                    if (!window.confirm('Delete this branch?')) return;
                                                    ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => updateField(`br_${idx}_${f}`, ''));
                                                    setActiveTransportBranches(prev => prev.filter(id => id !== idx));
                                                };
                                                return (
                                                    <div className="space-y-4">
                                                        <div className="flex items-center justify-between">
                                                            <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex-1 mr-4">
                                                                <p className="text-xs font-bold text-blue-700">{branches.length} branch(es) — Edit inline, then click "Update Branch Network" to save.</p>
                                                            </div>
                                                            <button onClick={handleAdd} className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg">
                                                                <span className="text-lg leading-none">+</span> Add Branch
                                                            </button>
                                                        </div>
                                                        {branches.length === 0 && (
                                                            <div className="text-center py-12 text-slate-400">
                                                                <p className="text-sm font-bold">No branches configured yet.</p>
                                                                <p className="text-xs mt-1">Click "Add Branch" to get started.</p>
                                                            </div>
                                                        )}
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                            {branches.map((branch) => (
                                                                <div key={branch.i} className="p-6 rounded-2xl border border-slate-100 bg-slate-50/50 space-y-4">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">BRANCH #{branch.i}</span>
                                                                        <button onClick={() => handleDelete(branch.i)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-[10px] font-black transition-all">🗑 Delete</button>
                                                                    </div>
                                                                    <BilingualInput label="Branch Name" englishValue={T[`br_${branch.i}_name`] || ''} arabicValue={T[`br_${branch.i}_name_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_name`, val)} onChangeArabic={val => updateField(`br_${branch.i}_name_ar`, val)} />
                                                                    <BilingualTextArea label="Address" englishValue={T[`br_${branch.i}_addr`] || ''} arabicValue={T[`br_${branch.i}_addr_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_addr`, val)} onChangeArabic={val => updateField(`br_${branch.i}_addr_ar`, val)} rows={2} />
                                                                    <div className="grid grid-cols-2 gap-4">
                                                                        <BilingualInput label="Phone" englishValue={T[`br_${branch.i}_phone`] || ''} arabicValue={T[`br_${branch.i}_phone_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_phone`, val)} onChangeArabic={val => updateField(`br_${branch.i}_phone_ar`, val)} />
                                                                        <BilingualInput label="Fax" englishValue={T[`br_${branch.i}_fax`] || ''} arabicValue={T[`br_${branch.i}_fax_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_fax`, val)} onChangeArabic={val => updateField(`br_${branch.i}_fax_ar`, val)} />
                                                                    </div>
                                                                    <BilingualInput label="GPS Coordinates" englishValue={T[`br_${branch.i}_coord`] || ''} arabicValue={T[`br_${branch.i}_coord_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_coord`, val)} onChangeArabic={val => updateField(`br_${branch.i}_coord_ar`, val)} placeholder="e.g. 23.575480, 58.354941" />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* === VALUES SECTION === */}
                                            {activeSection === 'values' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Values Title"
                                                        englishValue={content.Transport?.values_title}
                                                        arabicValue={content.Transport?.values_title_ar}
                                                        onChangeEnglish={val => updateField('values_title', val)}
                                                        onChangeArabic={val => updateField('values_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Values Description"
                                                        englishValue={content.Transport?.values_desc}
                                                        arabicValue={content.Transport?.values_desc_ar}
                                                        onChangeEnglish={val => updateField('values_desc', val)}
                                                        onChangeArabic={val => updateField('values_desc_ar', val)}
                                                        rows={2}
                                                    />
                                                    <div className="grid grid-cols-1 gap-6">
                                                        {[1, 2, 3, 4].map(id => (
                                                            <div key={id} className="p-6 border border-slate-100 rounded-xl bg-slate-50/50 space-y-4">
                                                                <BilingualInput 
                                                                    label={`Value ${id} Title`}
                                                                    englishValue={content.Transport?.[`val_${id}_title`]}
                                                                    arabicValue={content.Transport?.[`val_${id}_title_ar`]}
                                                                    onChangeEnglish={val => updateField(`val_${id}_title`, val)}
                                                                    onChangeArabic={val => updateField(`val_${id}_title_ar`, val)}
                                                                />
                                                                <BilingualTextArea 
                                                                    label={`Value ${id} Description`}
                                                                    englishValue={content.Transport?.[`val_${id}_desc`]}
                                                                    arabicValue={content.Transport?.[`val_${id}_desc_ar`]}
                                                                    onChangeEnglish={val => updateField(`val_${id}_desc`, val)}
                                                                    onChangeArabic={val => updateField(`val_${id}_desc_ar`, val)}
                                                                    rows={2}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* === CODE SECTION === */}
                                            {activeSection === 'code' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Title"
                                                        englishValue={content.Transport?.code_title}
                                                        arabicValue={content.Transport?.code_title_ar}
                                                        onChangeEnglish={val => updateField('code_title', val)}
                                                        onChangeArabic={val => updateField('code_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Intro Description"
                                                        englishValue={content.Transport?.code_desc}
                                                        arabicValue={content.Transport?.code_desc_ar}
                                                        onChangeEnglish={val => updateField('code_desc', val)}
                                                        onChangeArabic={val => updateField('code_desc_ar', val)}
                                                        rows={2}
                                                    />
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                        {[1, 2, 3, 4].map(id => (
                                                            <div key={id} className="p-6 border border-slate-100 rounded-xl bg-slate-50/50 space-y-4">
                                                                <BilingualInput 
                                                                    label={`Point ${id} Title`}
                                                                    englishValue={content.Transport?.[`code_${id}_title`]}
                                                                    arabicValue={content.Transport?.[`code_${id}_title_ar`]}
                                                                    onChangeEnglish={val => updateField(`code_${id}_title`, val)}
                                                                    onChangeArabic={val => updateField(`code_${id}_title_ar`, val)}
                                                                />
                                                                <BilingualTextArea 
                                                                    label={`Point ${id} Description`}
                                                                    englishValue={content.Transport?.[`code_${id}_desc`]}
                                                                    arabicValue={content.Transport?.[`code_${id}_desc_ar`]}
                                                                    onChangeEnglish={val => updateField(`code_${id}_desc`, val)}
                                                                    onChangeArabic={val => updateField(`code_${id}_desc_ar`, val)}
                                                                    rows={3}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <BilingualInput 
                                                        label="Footer Text"
                                                        englishValue={content.Transport?.code_footer}
                                                        arabicValue={content.Transport?.code_footer_ar}
                                                        onChangeEnglish={val => updateField('code_footer', val)}
                                                        onChangeArabic={val => updateField('code_footer_ar', val)}
                                                    />
                                                </div>
                                            )}

                                            {/* === APPROACH SECTION === */}
                                            {activeSection === 'approach' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Approach Section Title"
                                                        englishValue={content.Transport?.approach_title}
                                                        arabicValue={content.Transport?.approach_title_ar}
                                                        onChangeEnglish={val => updateField('approach_title', val)}
                                                        onChangeArabic={val => updateField('approach_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Approach Introduction"
                                                        englishValue={content.Transport?.approach_intro}
                                                        arabicValue={content.Transport?.approach_intro_ar}
                                                        onChangeEnglish={val => updateField('approach_intro', val)}
                                                        onChangeArabic={val => updateField('approach_intro_ar', val)}
                                                        rows={2}
                                                    />
                                                    <BilingualInput 
                                                        label="Approach Tagline"
                                                        englishValue={content.Transport?.approach_tagline}
                                                        arabicValue={content.Transport?.approach_tagline_ar}
                                                        onChangeEnglish={val => updateField('approach_tagline', val)}
                                                        onChangeArabic={val => updateField('approach_tagline_ar', val)}
                                                    />
                                                    <div className="grid grid-cols-1 gap-6">
                                                        {[1, 2, 3, 4, 5].map(id => (
                                                            <div key={id} className="p-6 border border-slate-100 rounded-xl bg-slate-50/50 space-y-4">
                                                                <BilingualInput 
                                                                    label={`Stage ${id} Title`}
                                                                    englishValue={content.Transport?.[`app_${id}_title`]}
                                                                    arabicValue={content.Transport?.[`app_${id}_title_ar`]}
                                                                    onChangeEnglish={val => updateField(`app_${id}_title`, val)}
                                                                    onChangeArabic={val => updateField(`app_${id}_title_ar`, val)}
                                                                />
                                                                <BilingualTextArea 
                                                                    label={`Stage ${id} Description`}
                                                                    englishValue={content.Transport?.[`app_${id}_desc`]}
                                                                    arabicValue={content.Transport?.[`app_${id}_desc_ar`]}
                                                                    onChangeEnglish={val => updateField(`app_${id}_desc`, val)}
                                                                    onChangeArabic={val => updateField(`app_${id}_desc_ar`, val)}
                                                                    rows={2}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <BilingualInput 
                                                        label="Approach Footer"
                                                        englishValue={content.Transport?.approach_footer}
                                                        arabicValue={content.Transport?.approach_footer_ar}
                                                        onChangeEnglish={val => updateField('approach_footer', val)}
                                                        onChangeArabic={val => updateField('approach_footer_ar', val)}
                                                    />
                                                </div>
                                            )}

                                            {/* === TECH SECTION === */}
                                            {activeSection === 'tech' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Innovation Title"
                                                        englishValue={content.Transport?.tech_title}
                                                        arabicValue={content.Transport?.tech_title_ar}
                                                        onChangeEnglish={val => updateField('tech_title', val)}
                                                        onChangeArabic={val => updateField('tech_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Innovation Intro"
                                                        englishValue={content.Transport?.tech_desc}
                                                        arabicValue={content.Transport?.tech_desc_ar}
                                                        onChangeEnglish={val => updateField('tech_desc', val)}
                                                        onChangeArabic={val => updateField('tech_desc_ar', val)}
                                                        rows={3}
                                                    />
                                                    <div className="pt-4 border-t border-slate-100 space-y-8">
                                                        <Label>Embedded Tech Systems (4 Systems)</Label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                            {[1, 2, 3, 4].map(id => (
                                                                <div key={id} className="p-6 border border-slate-100 rounded-xl bg-slate-50/50 space-y-4">
                                                                    <BilingualInput 
                                                                        label={`System ${id} Title`}
                                                                        englishValue={content.Transport?.[`tech_sys_${id}_title`]}
                                                                        arabicValue={content.Transport?.[`tech_sys_${id}_title_ar`]}
                                                                        onChangeEnglish={val => updateField(`tech_sys_${id}_title`, val)}
                                                                        onChangeArabic={val => updateField(`tech_sys_${id}_title_ar`, val)}
                                                                    />
                                                                    <BilingualTextArea 
                                                                        label={`System ${id} Description`}
                                                                        englishValue={content.Transport?.[`tech_sys_${id}_desc`]}
                                                                        arabicValue={content.Transport?.[`tech_sys_${id}_desc_ar`]}
                                                                        onChangeEnglish={val => updateField(`tech_sys_${id}_desc`, val)}
                                                                        onChangeArabic={val => updateField(`tech_sys_${id}_desc_ar`, val)}
                                                                        rows={3}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        <Label>Advantages Section Metadata</Label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                            <BilingualInput 
                                                                label="Advantages Heading"
                                                                englishValue={content.Transport?.tech_benefit_title || 'Customer Advantages'}
                                                                arabicValue={content.Transport?.tech_benefit_title_ar}
                                                                onChangeEnglish={val => updateField('tech_benefit_title', val)}
                                                                onChangeArabic={val => updateField('tech_benefit_title_ar', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Benefits Badge/Label"
                                                                englishValue={content.Transport?.tech_strategic_label || 'Strategic Value'}
                                                                arabicValue={content.Transport?.tech_strategic_label_ar}
                                                                onChangeEnglish={val => updateField('tech_strategic_label', val)}
                                                                onChangeArabic={val => updateField('tech_strategic_label_ar', val)}
                                                            />
                                                        </div>
                                                        <Label>Customer Advantages (3 Strategic Points)</Label>
                                                        <div className="grid grid-cols-1 gap-6">
                                                            {[1, 2, 3].map(id => (
                                                                <div key={id} className="p-8 border border-slate-100 rounded-2xl bg-slate-50/50 space-y-6">
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center font-black text-blue-600 shadow-sm border border-slate-100 text-sm shrink-0">{id}</div>
                                                                        <BilingualInput 
                                                                            label={`Benefit ${id} Title`}
                                                                            englishValue={content.Transport?.[`tech_bn_${id}_title`]}
                                                                            arabicValue={content.Transport?.[`tech_bn_${id}_title_ar`]}
                                                                            onChangeEnglish={val => updateField(`tech_bn_${id}_title`, val)}
                                                                            onChangeArabic={val => updateField(`tech_bn_${id}_title_ar`, val)}
                                                                        />
                                                                    </div>
                                                                    <BilingualTextArea 
                                                                        label={`Benefit ${id} Description`}
                                                                        englishValue={content.Transport?.[`tech_bn_${id}_desc`]}
                                                                        arabicValue={content.Transport?.[`tech_bn_${id}_desc_ar`]}
                                                                        onChangeEnglish={val => updateField(`tech_bn_${id}_desc`, val)}
                                                                        onChangeArabic={val => updateField(`tech_bn_${id}_desc_ar`, val)}
                                                                        rows={3}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* === AWARDS SECTION === */}
                                            {activeSection === 'awards' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Awards Section Title"
                                                        englishValue={content.Transport?.awards_title}
                                                        arabicValue={content.Transport?.awards_title_ar}
                                                        onChangeEnglish={val => updateField('awards_title', val)}
                                                        onChangeArabic={val => updateField('awards_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Awards Intro Paragraph"
                                                        englishValue={content.Transport?.awards_desc}
                                                        arabicValue={content.Transport?.awards_desc_ar}
                                                        onChangeEnglish={val => updateField('awards_desc', val)}
                                                        onChangeArabic={val => updateField('awards_desc_ar', val)}
                                                        rows={3}
                                                    />
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        {[1, 2, 3, 4, 5, 6].map(id => (
                                                            <BilingualInput 
                                                                key={id}
                                                                label={`Award / Recognition ${id}`}
                                                                englishValue={content.Transport?.[`award_pt_${id}_text`]}
                                                                arabicValue={content.Transport?.[`award_pt_${id}_text_ar`]}
                                                                onChangeEnglish={val => updateField(`award_pt_${id}_text`, val)}
                                                                onChangeArabic={val => updateField(`award_pt_${id}_text_ar`, val)}
                                                            />
                                                        ))}
                                                    </div>
                                                    <BilingualInput 
                                                        label="Footer Tagline"
                                                        englishValue={content.Transport?.award_footer}
                                                        arabicValue={content.Transport?.award_footer_ar}
                                                        onChangeEnglish={val => updateField('award_footer', val)}
                                                        onChangeArabic={val => updateField('award_footer_ar', val)}
                                                    />
                                                </div>
                                            )}

                                            {/* === CLIENTS SECTION === */}
                                            {activeSection === 'clients' && (
                                                <ClientsEditorFields
                                                    data={content.Transport}
                                                    updateField={updateField}
                                                    defaultDescription="Corporate, government, and industrial clients served by Hamdan Transport Division."
                                                />
                                            )}

                                            {/* === CONTACT SECTION === */}
                                            {activeSection === 'contact' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Address"
                                                        englishValue={content.Transport?.contact_address}
                                                        arabicValue={content.Transport?.contact_address_ar}
                                                        onChangeEnglish={val => updateField('contact_address', val)}
                                                        onChangeArabic={val => updateField('contact_address_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Phone"
                                                        englishValue={content.Transport?.contact_phone}
                                                        arabicValue={content.Transport?.contact_phone_ar}
                                                        onChangeEnglish={val => updateField('contact_phone', val)}
                                                        onChangeArabic={val => updateField('contact_phone_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Fax"
                                                        englishValue={content.Transport?.contact_fax}
                                                        arabicValue={content.Transport?.contact_fax_ar}
                                                        onChangeEnglish={val => updateField('contact_fax', val)}
                                                        onChangeArabic={val => updateField('contact_fax_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Email"
                                                        englishValue={content.Transport?.contact_email}
                                                        arabicValue={content.Transport?.contact_email_ar}
                                                        onChangeEnglish={val => updateField('contact_email', val)}
                                                        onChangeArabic={val => updateField('contact_email_ar', val)}
                                                    />
                                                </div>
                                            )}

                                            {/* === GALLERY SECTION === */}
                                            {activeSection === 'gallery' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Gallery Title"
                                                        englishValue={content.Transport?.gallery_title}
                                                        arabicValue={content.Transport?.gallery_title_ar}
                                                        onChangeEnglish={val => updateField('gallery_title', val)}
                                                        onChangeArabic={val => updateField('gallery_title_ar', val)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div key="media" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} className="space-y-12 pb-24">
                                        <div className="flex items-center justify-between border-b border-slate-200 pb-8">
                                            <div>
                                                <h3 className="text-3xl font-black text-slate-800 tracking-tight">Media Repository</h3>
                                                <p className="text-slate-500 text-sm font-medium">Logistics and fleet visual assets</p>
                                            </div>
                                            <button
                                                onClick={saveAllPendingImages}
                                                disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-blue-200 disabled:bg-slate-300 active:scale-95 flex items-center gap-2"
                                            >
                                                <Save className="w-4 h-4" />
                                                <span>Push Media Update</span>
                                            </button>
                                        </div>

                                        <div className="space-y-20">
                                            <div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b pb-2">Hero Resources</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
                                                    <UploadBox keyName="banner_1" label="Banner Slide 1" currentImage={content.Transport?.banner_1} />
                                                    <UploadBox keyName="banner_2" label="Banner Slide 2" currentImage={content.Transport?.banner_2} />
                                                    <UploadBox keyName="banner_3" label="Banner Slide 3" currentImage={content.Transport?.banner_3} />
                                                </div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b pb-2">Core Imgs</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                                    <UploadBox keyName="about_image" label="Profile/About Backdrop" currentImage={content.Transport?.about_image} />
                                                    <UploadBox keyName="tech_image" label="Innovation Centerpiece" currentImage={content.Transport?.tech_image} />
                                                </div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b pb-2">Service Images</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                    <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={content.Transport?.svc_1_image} />
                                                    <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={content.Transport?.svc_2_image} />
                                                    <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={content.Transport?.svc_3_image} />
                                                </div>
                                            </div>

                                            <ClientLogoUploadGrid
                                                data={content.Transport}
                                                pendingImages={pendingImages}
                                                onSelect={handleImageSelect}
                                                listKey="clients_list_text"
                                            />

                                            <div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b pb-2 text-rose-600">Safety / QHSE Showcase (4 Images)</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                                    {[1, 2, 3, 4].map(i => (
                                                        <UploadBox key={i} keyName={`qhse_img_${i}`} label={`Safety Image ${i}`} currentImage={content.Transport?.[`qhse_img_${i}`]} />
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b pb-2 text-blue-600">Awards Showcase (5 Slides)</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                                                    {[1, 2, 3, 4, 5].map(i => (
                                                        <UploadBox key={i} keyName={`award_img_${i}`} label={`Award Frame ${i}`} currentImage={content.Transport?.[`award_img_${i}`]} />
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b pb-2 text-purple-600">History / Events Showcase</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 mb-8 max-w-sm">
                                                    {[1].map(i => (
                                                        <div key={i} className="space-y-4">
                                                            <UploadBox keyName={`hist_${i}_img_1`} label={`History ${i} Img A`} currentImage={content.Transport?.[(`hist_${i}_img_1`)]} />
                                                            <UploadBox keyName={`hist_${i}_img_2`} label={`History ${i} Img B`} currentImage={content.Transport?.[(`hist_${i}_img_2`)]} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <div className="flex justify-between items-end mb-6 border-b pb-2">
                                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest text-emerald-600">Gallery Full Upload (Up to 150 images)</h4>
                                                    <label className="cursor-pointer bg-emerald-100 text-emerald-700 hover:bg-emerald-200 px-4 py-2 rounded-xl text-xs font-bold transition-colors shadow-sm">
                                                        Select Multiple Images
                                                        <input type="file" multiple accept="image/*" className="hidden" onChange={e => {
                                                            if (e.target.files) handleMultipleImageSelect('gal', e.target.files);
                                                        }} />
                                                    </label>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                                    {Array.from({ length: 150 }, (_, i) => i + 1).map(i => {
                                                        const key = `gal_${i}`;
                                                        if (!content.Transport?.[key] && !pendingImages[key]) return null;
                                                        return (
                                                            <UploadBox
                                                                key={i}
                                                                keyName={key}
                                                                label={`Gallery ${i}`}
                                                                currentImage={content.Transport?.[key]}
                                                                onDelete={content.Transport?.[key] ? () => handleDeleteGalleryKey(key) : undefined}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                {Object.keys(content.Transport || {}).filter(k => k.startsWith('gal_')).length === 0 && Object.keys(pendingImages).filter(k => k.startsWith('gal_')).length === 0 && (
                                                    <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-3xl">
                                                        <ImageIcon className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                                                        <p className="text-sm font-bold text-slate-500">No gallery images live currently</p>
                                                        <p className="text-xs font-medium text-slate-400">Use the multiple upload button above to add some</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        )}
                        <div className="h-20" />
                    </div>
                </main>
            </div>

            <AnimatePresence>
                {message.text && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className={`fixed bottom-10 right-10 z-[100] px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-4 ${message.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
                    >
                        {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        <span className="font-bold text-sm tracking-tight">{message.text}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminTransport;
